import React, { createContext, useContext, useState, useCallback } from "react"
import { Machine } from "./types"
// import { WizardData, LoadWizardData, SaveWizardData, CleanWizardData } from "./Model"

export interface WizardData {
  qrCode: string | undefined
  userGroup: string | undefined
  machine: Machine | undefined
}

export const emptyWizardData = (qrCode: string | undefined, machine: Machine): WizardData => {
  return {
    qrCode: qrCode,
    userGroup: undefined,
    machine
  }
}

interface WizardContextType {
  wizardData: WizardData
  setWizardData: (data: WizardData) => void
  updateWizardData: (changes: Partial<WizardData>) => void
  resetWizardData: () => void
  step: number
  setStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void
}

const WizardContext = createContext<WizardContextType | undefined>(undefined)

export const WizardProvider: React.FC<{ 
  initialQrCode?: string; 
  initialMachine?: Machine | undefined
  maxStep: number; 
  children: React.ReactNode 
}> = ({ initialQrCode, initialMachine, maxStep, children }) => {
  const initialData = emptyWizardData(initialQrCode ?? "", initialMachine || {})
  const [wizardData, setWizardDataState] = useState<WizardData>(initialData)
  const [step, setStep] = useState<number>(0)

  // Set & persist
  const setWizardData = useCallback((data: WizardData) => {
    setWizardDataState(data)
  }, [])

  // Patch & persist
  const updateWizardData = useCallback((changes: Partial<WizardData>) => {
    setWizardDataState((current: WizardData) => {
      const updated = { ...current, ...changes }
      return updated
    })
  }, [])

  // Reset wizard
  const resetWizardData = useCallback(() => {
    const reset = emptyWizardData(initialQrCode, {})
    setWizardDataState(reset)
    setStep(0)
  }, [initialQrCode])

  // Step handlers
  const nextStep = useCallback(() => setStep(s => s < maxStep ? s + 1: s), [])
  const prevStep = useCallback(() => setStep(s => (s > 0 ? s - 1 : 0)), [])

  return (
    <WizardContext.Provider
      value={{
        wizardData,
        setWizardData,
        updateWizardData,
        resetWizardData,
        step,
        setStep,
        nextStep,
        prevStep,
      }}
    >
      {children}
    </WizardContext.Provider>
  )
}

export function useWizard() {
  const ctx = useContext(WizardContext)
  if (!ctx) throw new Error("useWizard must be used within a WizardProvider")
  return ctx
}